import React from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link'; // Đã thêm Link component
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';
import { OptionModel } from 'src/types/api-response';
import { CaySam, CaySamNhatKy } from 'src/types/caysam';
import useCaySamFilterOptions from '../../../utils/use-caysam-filter-options';
import TimeLine, { TimelineEntry } from '../components/time-line';
import useGetCaySam from '../hooks/use-get-caysam';
import { useCaySam } from '../hooks/use-losam';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string; 
  lotName: string;
  lotCode: string;
  gardenName: string;
  positionLabel: string;
  caySamId?: string | null; 
  caySamData: CaySam | null;
  onUpdateCaySam: (caySamData: Partial<CaySam>) => void;
  loaiTuoiOptions: OptionModel[];
  diemSucKhoeOptions?: OptionModel[];
  loading: boolean;
  isEdit: boolean;
  onDelete: () => void;
  onSave: () => void;
  readOnly?: boolean;
  onRefresh?: () => void;
};

export default function CaySamDrawer({ open, onClose, title, lotName, lotCode, gardenName, positionLabel, caySamId, caySamData, onUpdateCaySam, loaiTuoiOptions, diemSucKhoeOptions, loading, onSave, readOnly, isEdit, onDelete, onRefresh }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { tinhTrangOptions, diemSucKhoeOptions: hookDiemOptions } = useCaySamFilterOptions(); 
  const mergedDiemOptions = diemSucKhoeOptions && diemSucKhoeOptions.length > 0 ? diemSucKhoeOptions : hookDiemOptions;
  const { getCaySamNhatKy, caySamNhatKy, loadingNhatKy, errorNhatKy } = useGetCaySam();
  const { loading: caySamLoading, error: caySamError } = useCaySam();

  // Sync fetched nhat ky from hook into local CaySam data
  React.useEffect(() => {
    if (caySamNhatKy && caySamNhatKy.length > 0) {
      onUpdateCaySam({ caySamNhatKys: caySamNhatKy });
    }
  }, [caySamNhatKy, onUpdateCaySam, enqueueSnackbar]);

  React.useEffect(() => {
    if (errorNhatKy) {
      enqueueSnackbar(errorNhatKy, { variant: 'error' });
    }
  }, [errorNhatKy, enqueueSnackbar]);

  React.useEffect(() => {
    if (caySamError) {
      enqueueSnackbar(caySamError, { variant: 'error' });
    }
  }, [caySamError, enqueueSnackbar]);

  // Latest nhật ký trong THÁNG HIỆN TẠI (nếu không có, coi như không có dữ liệu để người dùng thêm mới)
  const latestNhatKyThisMonth = React.useMemo(() => {
    const list = caySamData?.caySamNhatKys?.filter((x): x is CaySamNhatKy => !!x) || [];
    if (!list.length) return null;
    const now = new Date();
    const sameMonth = (d: Date) => d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    const listThisMonth = list.filter((nk) => sameMonth(new Date(nk.ngayGhi as unknown as string)));
    if (!listThisMonth.length) return null;
    return listThisMonth.reduce((acc: CaySamNhatKy | null, cur: CaySamNhatKy) => {
      if (!acc) return cur;
      return new Date(cur.ngayGhi as unknown as string).getTime() > new Date(acc.ngayGhi as unknown as string).getTime() ? cur : acc;
    }, null as CaySamNhatKy | null);
  }, [caySamData?.caySamNhatKys]);

  // Mã cây sâm hiện tại (chỉ đọc)
  const currentMaCaySam = caySamData?.maCaySam || '';

  // State để lưu dữ liệu nhật ký mới khi chưa có nhật ký trong tháng
  const [newNhatKyData, setNewNhatKyData] = React.useState<Partial<CaySamNhatKy>>({
    tinhTrang: 1,
    diemSucKhoe: 0,
    soLa: 0,
    hinhAnhTongQuan: '',
    hinhAnhChiTiet: '',
  });

  // Lấy dữ liệu hiện tại (từ nhật ký tháng này hoặc từ form mới)
  const currentNhatKyData = React.useMemo(() => {
    if (latestNhatKyThisMonth) {
      return latestNhatKyThisMonth;
    }
    return newNhatKyData;
  }, [latestNhatKyThisMonth, newNhatKyData]);

  // Convert CaySamNhatKy data to TimelineEntry format
  const timelineItems: TimelineEntry[] = React.useMemo(() => {
    if (!caySamData?.caySamNhatKys) return [];
    
    return caySamData.caySamNhatKys
      .filter((nhatKy): nhatKy is CaySamNhatKy => nhatKy !== null)
      .map((nhatKy) => {
        const date = new Date(nhatKy.ngayGhi);
        const timeStr = date.toLocaleString('vi-VN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        const tinhTrangText = tinhTrangOptions.find(opt => Number(opt.value) === nhatKy.tinhTrang)?.text || 'Không xác định';
        const diemSucKhoeText = mergedDiemOptions?.find(opt => Number(opt.value) === nhatKy.diemSucKhoe)?.text || 'Không xác định';
        
        const note = `Tình trạng: ${tinhTrangText}, Điểm sức khỏe: ${diemSucKhoeText}, Số lá: ${nhatKy.soLa}`;
        
        const images: [string, string] = [
          nhatKy.hinhAnhTongQuan || '/assets/placeholder.svg',
          nhatKy.hinhAnhChiTiet || '/assets/placeholder.svg'
        ];
        
        return {
          time: timeStr,
          note,  
          images
        };
      })
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()); // Sort by newest first
  }, [caySamData?.caySamNhatKys, tinhTrangOptions, mergedDiemOptions]);

  // If fetched from hook, prefer showing that timeline immediately
  const [timelineLoaded, setTimelineLoaded] = React.useState<TimelineEntry[] | null>(null);
  
  // State to control timeline visibility
  const [showTimeline, setShowTimeline] = React.useState(false);

  React.useEffect(() => {
    if (caySamNhatKy && caySamNhatKy.length > 0) {
      const entries = caySamNhatKy.map((nhatKy: CaySamNhatKy) => {
        const date = new Date(nhatKy.ngayGhi as unknown as string);
        const timeStr = date.toLocaleString('vi-VN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
        const tinhTrangText = tinhTrangOptions.find(opt => Number(opt.value) === nhatKy.tinhTrang)?.text || 'Không xác định';
        const diemSucKhoeText = mergedDiemOptions?.find(opt => Number(opt.value) === nhatKy.diemSucKhoe)?.text || 'Không xác định';
        const note = `Tình trạng: ${tinhTrangText}, Điểm sức khỏe: ${diemSucKhoeText}, Số lá: ${nhatKy.soLa}`;
        const images: [string, string] = [
          nhatKy.hinhAnhTongQuan || '/assets/placeholder.svg',
          nhatKy.hinhAnhChiTiet || '/assets/placeholder.svg',
        ];
        return { time: timeStr, note, images } as TimelineEntry;
      });
      setTimelineLoaded(entries);
    }
  }, [caySamNhatKy, tinhTrangOptions, mergedDiemOptions]);
  
  const timelineToShow = timelineLoaded ?? timelineItems;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 800 } } }}>
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onClose} sx={{ mr: 1 }}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {title }  
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />
 
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Thông tin vị trí
              </Typography>
 
              <Stack direction="row" spacing={2}>
              <TextField fullWidth label="Vườn trồng" value={gardenName} disabled size="small" InputProps={{ startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} /> }} />
              <TextField fullWidth label="Lô sâm" value={lotName} disabled size="small" InputProps={{ startAdornment: <Iconify icon="solar:home-bold" sx={{ mr: 1 }} /> }} />
              <TextField fullWidth label="Vị trí" value={positionLabel} disabled size="small" InputProps={{ startAdornment: <Iconify icon="solar:map-point-bold" sx={{ mr: 1 }} /> }} />
              </Stack>

            </Box>

            <Divider />
   
            {/* Thông tin cơ bản Cây sâm */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Thông tin cây sâm
              </Typography>

              <Stack spacing={2}>
                <TextField 
                  fullWidth 
                  label="Mã cây sâm" 
                  value={currentMaCaySam} 
                  required 
                  size="small"  
                  disabled
                  helperText="Mã cây sâm hiện tại (không thể thay đổi)"
                  InputProps={{ startAdornment: <Iconify icon="solar:medal-ribbons-star-bold" sx={{ mr: 1 }} /> }} 
                />

                <FormControl fullWidth size="small" disabled>
                  <InputLabel>Tuổi cây</InputLabel>
                  <Select 
                    value={caySamData?.tuoiCay_ID ?? ''} 
                    label="Tuổi cây" 
                    startAdornment={<Iconify icon="solar:calendar-bold" sx={{ mr: 1 }} />}
                  > 
                    {loaiTuoiOptions.map((opt) => (
                      <MenuItem key={opt.value} value={Number(opt.value)}>{opt.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl> 
              </Stack>
            </Box>
              {!(readOnly ?? !isEdit) && (
                <Stack spacing={2}> 
                  <FormControl fullWidth size="small">
                    <InputLabel>Tình trạng</InputLabel>
                    <Select 
                      value={currentNhatKyData?.tinhTrang ?? ''} 
                      label="Tình trạng"   
                      onChange={(e) => {
                        if (latestNhatKyThisMonth) {
                          const next = (caySamData?.caySamNhatKys || [])
                            .map((nhatKy) => (nhatKy && nhatKy === latestNhatKyThisMonth
                              ? ({ ...nhatKy, tinhTrang: Number(e.target.value) } as CaySamNhatKy)
                              : nhatKy))
                            .filter((x): x is CaySamNhatKy => !!x);
                          onUpdateCaySam({ caySamNhatKys: next });
                        } else {
                          setNewNhatKyData(prev => ({ ...prev, tinhTrang: Number(e.target.value) }));
                        }
                      }} 
                      startAdornment={<Iconify icon="solar:flag-2-bold" sx={{ mr: 1 }} />}
                    >
                      {tinhTrangOptions.map((opt) => (
                        <MenuItem key={opt.value} value={Number(opt.value)}>{opt.text}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Điểm sức khỏe</InputLabel>
                    <Select 
                      value={currentNhatKyData?.diemSucKhoe ?? ''}
                      label="Điểm sức khỏe" 
                      onChange={(e) => {
                        if (latestNhatKyThisMonth) {
                          const next = (caySamData?.caySamNhatKys || [])
                            .map((nhatKy) => (nhatKy && nhatKy === latestNhatKyThisMonth
                              ? ({ ...nhatKy, diemSucKhoe: Number(e.target.value) } as CaySamNhatKy)
                              : nhatKy))
                            .filter((x): x is CaySamNhatKy => !!x);
                          onUpdateCaySam({ caySamNhatKys: next }); 
                        } else {
                          setNewNhatKyData(prev => ({ ...prev, diemSucKhoe: Number(e.target.value) }));
                        }
                      }}
                      startAdornment={<Iconify icon="solar:check-circle-bold" sx={{ mr: 1 }} />}
                    >
                      {mergedDiemOptions?.map((opt) => (
                        <MenuItem key={opt.value} value={Number(opt.value)}>{opt.text}</MenuItem>
                      ))}
                    </Select> 
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Số lá"
                    value={currentNhatKyData?.soLa ?? ''} 
                    onChange={(e) => {
                      if (latestNhatKyThisMonth) {
                        const next = (caySamData?.caySamNhatKys || [])
                          .map((nhatKy) => (nhatKy && nhatKy === latestNhatKyThisMonth
                            ? ({ ...nhatKy, soLa: Number(e.target.value) || 0 } as CaySamNhatKy)
                            : nhatKy))
                          .filter((x): x is CaySamNhatKy => !!x);
                        onUpdateCaySam({ caySamNhatKys: next });
                      } else {
                        setNewNhatKyData(prev => ({ ...prev, soLa: Number(e.target.value) || 0 }));
                      }
                    }}
                    size="small" 
                    InputProps={{ startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} /> }}
                  />
                </Stack>
              )}
            <Divider /> 

            {/* Thông tin sinh trưởng */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Thông tin sinh trưởng
                </Typography>
              </Box>

              {/* KHỐI 1: ẢNH TỔNG QUAN VÀ CHI TIẾT */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {/* Ảnh tổng quát */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Ảnh tổng quát
                  </Typography>
                  {currentNhatKyData?.hinhAnhTongQuan ? (
                    <Box component="img" src={currentNhatKyData.hinhAnhTongQuan} alt="Ảnh tổng quát" sx={{ width: '100%',height: 140, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }} />
                  ) : (
                    <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300' }}>
                      <Typography variant="caption" color="text.secondary">Không có ảnh</Typography>
                    </Box>
                  )}
                </Box>

                {/* Ảnh chi tiết */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Ảnh chi tiết
                  </Typography>
                  {currentNhatKyData?.hinhAnhChiTiet ? (
                    <Box component="img" src={currentNhatKyData.hinhAnhChiTiet} alt="Ảnh chi tiết" sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }} />
                  ) : (
                    <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300' }}>
                      <Typography variant="caption" color="text.secondary">Không có ảnh</Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
 
             
            </Box>

            <Divider />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Lịch sử sinh trưởng
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Iconify icon="solar:history-bold" />}
                  onClick={() => {
                    if (caySamId) {
                      getCaySamNhatKy(String(caySamId));
                      setShowTimeline(true);
                    }
                  }}
                  disabled={!caySamData?.caySam_ID || loadingNhatKy}
                >
                  {loadingNhatKy ? 'Đang tải...' : 'Xem lịch sử'}
                </Button>
              </Box>
              {(() => {
                if (showTimeline && timelineToShow.length > 0) {
                  return <TimeLine title="" itemsNhatKy={(caySamData?.caySamNhatKys || []).filter((x): x is CaySamNhatKy => !!x)} />;
                }
                if (showTimeline) {
                  return (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">Chưa có dữ liệu sinh trưởng</Typography>
                    </Box>
                  );
                }
                return (
                  <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Nhấn &quot;Xem lịch sử&quot; để hiển thị dữ liệu sinh trưởng</Typography>
                  </Box>
                );
              })()}
            </Box>
           
          </Stack> 
        </Box>
       
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={onClose} fullWidth>
              Đóng
            </Button>
            {/* Hidden save button as requested */}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  ); 
}